import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { DashboardShell } from "@/components/DashboardShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
    ChevronLeft,
    CheckCircle2,
    XCircle,
    RotateCcw,
    Download,
    Mail,
    Building2,
    User,
    BookOpenText,
    UploadCloud,
    PenTool,
    ExternalLink,
    FileSignature,
    ShieldAlert,
    AlertTriangle,
    Eye,
    Truck,
} from "lucide-react";
import GenerateContractDialog from "@/components/GenerateContractDialog";
import AssignEquipmentDialog from "@/components/AssignEquipmentDialog";
import { EquipmentTypeBadge } from "@/components/EquipmentTypeBadge";
import { FilePreviewModal } from "@/components/FilePreviewModal";
import { UploadStatusBadge } from "@/components/UploadStatusBadge";
import { SAFER_RISK_LABELS } from "@/lib/saferRisk";
import { classifyFmcsaField } from "@/lib/fmcsaVerify";
import { formatEin, normalizeEin } from "@/lib/ein";

import { getBackendBase } from "@/lib/api";
import { usePermission } from "@/lib/usePermission";
import CancelSubmissionModal from "@/components/CancelSubmissionModal";  // Phase CANCEL-DRAFT (Part B / FE)
const BACKEND_URL = getBackendBase();  // Phase APEX-C — same-origin only. See lib/api.js.

const STATUS_PILL = {
    submitted: "bg-amber-50 text-amber-900 ring-amber-200",
    approved: "bg-emerald-50 text-emerald-900 ring-emerald-200",
    rejected: "bg-red-50 text-red-700 ring-red-200",
    draft: "bg-slate-100 text-slate-700 ring-slate-200",
    cancelled: "bg-slate-100 text-slate-500 ring-slate-200",
};

function fmtDateTime(s) {
    if (!s) return "—";
    try {
        return new Date(s).toLocaleString();
    } catch {
        return s;
    }
}

function FieldRow({ label, value, required, fmcsaState, testId }) {
    return (
        <div className="px-4 py-2.5 flex justify-between gap-4 text-sm" data-testid={testId}>
            <span className="text-slate-500">
                {label}
                {required && <span className="text-red-500 ml-1">*</span>}
            </span>
            <span className="text-slate-900 text-right break-words max-w-[60%] inline-flex items-center justify-end gap-2 flex-wrap">
                <span>{value || "—"}</span>
                {/* Phase QCM-EXT — verified-from-FMCSA pill or
                    customer-entered chip. Hidden when FMCSA has no
                    reference value or the field is empty + unmatched. */}
                {fmcsaState === "verified" && (
                    <span
                        data-testid={`${testId || "field-row"}-fmcsa-verified-pill`}
                        title="Matches the current FMCSA snapshot for this carrier"
                        className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-800"
                    >
                        ✓ Verified from FMCSA
                    </span>
                )}
                {fmcsaState === "diverged" && (
                    <span
                        data-testid={`${testId || "field-row"}-fmcsa-diverged-chip`}
                        title="Customer edited this away from the FMCSA-reported value"
                        className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-900"
                    >
                        Customer-entered
                    </span>
                )}
            </span>
        </div>
    );
}

function SectionCard({ icon: Icon, title, children }) {
    return (
        <Card>
            <CardContent className="p-0">
                <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
                    <Icon size={16} className="text-slate-500" />
                    <h3 className="font-display font-semibold text-slate-900">{title}</h3>
                </div>
                <div className="divide-y divide-slate-100">{children}</div>
            </CardContent>
        </Card>
    );
}

const GROUP_LABELS = {
    company: "Company",
    guarantor: "Guarantor",
    references: "References",
};

export default function AdminSubmissionDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [catalog, setCatalog] = useState(null);
    const [sub, setSub] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [dialog, setDialog] = useState(null); // 'approve' | 'reject' | 'reopen' | null
    const [note, setNote] = useState("");
    const [contractDialogOpen, setContractDialogOpen] = useState(false);
    const [assignEquipmentOpen, setAssignEquipmentOpen] = useState(false);
    // Task 26-A — resend-invitation dialog state.
    const [showResendInvite, setShowResendInvite] = useState(false);
    const [resendInviteNote, setResendInviteNote] = useState("");
    const [resendingInvite, setResendingInvite] = useState(false);
    // Task 28-D — archive-awaiting-reassignment dialog state.
    const [showArchiveAwaiting, setShowArchiveAwaiting] = useState(false);
    const [archiveAwaitingNote, setArchiveAwaitingNote] = useState("");
    const [archiveAwaitingVisible, setArchiveAwaitingVisible] = useState(false);
    const [archivingAwaiting, setArchivingAwaiting] = useState(false);
    // Phase CANCEL-DRAFT (Part B / FE) — admin-side cancel draft modal.
    const [cancelDraftOpen, setCancelDraftOpen] = useState(false);
    const { hasPermission } = usePermission();
    // Phase AE — best-effort display name for the success toast +
    // the equipment-assigned banner copy. Falls back gracefully.
    const customerNameFromSub = (() => {
        if (!sub) return "";
        return sub?.data?.company_name || sub?.customer_name || "";
    })();
    // Task 26-A — best-effort customer email for the resend-invite
    // confirmation modal. SubmissionPublic doesn't expose it directly,
    // so we fall back through the linked customer/user fields.
    const customerEmailFromSub = (() => {
        if (!sub) return "";
        return sub?.customer_email || sub?.data?.billing_contact_email || "";
    })();
    const [saferPreflight, setSaferPreflight] = useState(null); // {kind:'risk'|'unavailable'|'ok', reasons, legal_name}
    // Phase QCM-EXT — cached FMCSA snapshot used to compute the
    // verified-from-FMCSA pills + customer-entered chips on the review
    // screen. Best-effort; null when no DOT, no cached snapshot, or the
    // endpoint errored. Read-only — never triggers a live FMCSA fetch.
    const [fmcsaSnap, setFmcsaSnap] = useState(null);
    // Phase DP — per-file preview / approval modal state.
    const [previewFile, setPreviewFile] = useState(null);
    const previewOpen = !!previewFile;

    const reload = async () => {
        setLoading(true);
        try {
            const [c, s] = await Promise.all([
                api.get("/fields/catalog"),
                api.get(`/admin/submissions/${id}`),
            ]);
            setCatalog(c.data);
            setSub(s.data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // Phase QCM-EXT — fetch the cached FMCSA snapshot for the review
    // pills. Silent on errors / no-data so missing snapshots don't
    // bother the reviewer.
    useEffect(() => {
        let cancelled = false;
        api.get(`/admin/submissions/${id}/fmcsa-snapshot`)
            .then(({ data }) => {
                if (cancelled) return;
                setFmcsaSnap(data?.found ? (data.fmcsa || null) : null);
            })
            .catch(() => {
                if (!cancelled) setFmcsaSnap(null);
            });
        return () => { cancelled = true; };
    }, [id]);

    // Map a submission field key → the corresponding FMCSA-snapshot
    // field. ``null`` for keys that have no FMCSA reference.
    const fmcsaFieldFor = (key) => {
        if (!fmcsaSnap) return null;
        switch (key) {
            case "ein_tax_id": return fmcsaSnap.ein;
            case "company_name":
            case "legal_name":
                return fmcsaSnap.legal_name;
            case "dba":
            case "dba_name":
                return fmcsaSnap.dba_name;
            case "company_address":
            case "physical_address":
                return fmcsaSnap.physical_address;
            case "mailing_address":
                return fmcsaSnap.mailing_address;
            case "company_phone":
            case "phone":
                return fmcsaSnap.phone;
            default:
                return null;
        }
    };

    const cfg = sub?.field_config || {};
    const grouped = useMemo(() => {
        if (!catalog || !sub) return {};
        const out = {};
        for (const f of catalog.fields) {
            if (cfg[f.key] === "hidden") continue;
            (out[f.group] = out[f.group] || []).push(f);
        }
        return out;
    }, [catalog, sub, cfg]);

    const renderValue = (f) => {
        const v = sub.data?.[f.key];
        if (f.type === "composite" && v && typeof v === "object") {
            return (
                <span>
                    {f.subfields
                        .map((s) => v[s.key])
                        .filter(Boolean)
                        .join(" · ") || "—"}
                </span>
            );
        }
        return v || "—";
    };

    const downloadPdf = async () => {
        try {
            const resp = await api.get(`/submissions/${id}/pdf`, { responseType: "blob" });
            const blob = new Blob([resp.data], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `submission-${id}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const downloadUpload = async (u) => {
        try {
            const resp = await api.get(`/submissions/${id}/uploads/${u.id}`, { responseType: "blob" });
            const url = URL.createObjectURL(resp.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = u.filename;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const openDialog = async (kind) => {
        setNote("");
        setSaferPreflight(null);
        setDialog(kind);
        if (kind !== "approve") return;
        const usdot = sub?.data?.usdot_number;
        if (!usdot) return;
        // 3s-bounded SAFER pre-flight — never blocks the admin.
        try {
            const controller = new AbortController();
            const timer = setTimeout(() => controller.abort(), 3000);
            const { data } = await api.get("/fmcsa/lookup", {
                params: { usdot },
                signal: controller.signal,
            });
            clearTimeout(timer);
            const status = (data.operating_status || "").toUpperCase();
            const auth = (data.operating_authority_status || "").toUpperCase();
            const reasons = [];
            if (status.includes("OUT OF SERVICE") || data.out_of_service_date) reasons.push("OUT_OF_SERVICE");
            if (status.includes("NOT AUTHORIZED") || auth.includes("NOT AUTHORIZED") || status.includes("INACTIVE")) reasons.push("NOT_AUTHORIZED");
            if (auth.includes("REVOKED") || auth.includes("SUSPENDED")) reasons.push("AUTHORITY_REVOKED");
            if (reasons.length > 0) {
                setSaferPreflight({ kind: "risk", reasons, legal_name: data.legal_name, out_of_service_date: data.out_of_service_date });
            } else {
                setSaferPreflight({ kind: "ok", legal_name: data.legal_name });
            }
        } catch {
            setSaferPreflight({ kind: "unavailable" });
        }
    };

    const submitAction = async () => {
        if (!dialog) return;
        if ((dialog === "reject" || dialog === "reopen") && !note.trim()) {
            toast.error("A note is required");
            return;
        }
        setBusy(true);
        try {
            const body = note.trim() ? { review_note: note.trim() } : {};
            await api.post(`/admin/submissions/${id}/${dialog}`, body);
            toast.success(
                dialog === "approve"
                    ? "Customer approved. Go to the customer's Contracts tab to create a contract."
                    : dialog === "reject"
                      ? "Application rejected"
                      : "Application reopened — customer notified"
            );
            setDialog(null);
            setNote("");
            await reload();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    // Task 26-A — call /admin/submissions/{id}/resend-invitation.
    const onResendInvitation = async () => {
        setResendingInvite(true);
        try {
            const resp = await api.post(
                `/admin/submissions/${id}/resend-invitation`,
                resendInviteNote.trim() ? { note: resendInviteNote.trim() } : {},
            );
            const tgt = resp?.customer_email || resp?.data?.customer_email || customerEmailFromSub || "the customer";
            toast.success(`Invitation resent to ${tgt}`);
            setShowResendInvite(false);
            setResendInviteNote("");
            await reload();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setResendingInvite(false);
        }
    };

    // Task 28-D — archive an awaiting_reassignment submission.
    const onArchiveAwaiting = async () => {
        setArchivingAwaiting(true);
        try {
            await api.post(
                `/admin/submissions/${id}/archive-awaiting-reassignment`,
                {
                    note: archiveAwaitingNote.trim() || null,
                    show_reason_to_customer: archiveAwaitingVisible,
                },
            );
            toast.success("Submission archived");
            setShowArchiveAwaiting(false);
            setArchiveAwaitingNote("");
            setArchiveAwaitingVisible(false);
            await reload();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setArchivingAwaiting(false);
        }
    };

    if (loading) {
        return (
            <DashboardShell>
                <p className="text-sm text-slate-500" data-testid="admin-submission-loading">Loading submission…</p>
            </DashboardShell>
        );
    }
    if (!sub || !catalog) {
        return (
            <DashboardShell>
                <p className="text-sm text-red-600" data-testid="admin-submission-error">Submission not found.</p>
                <Button asChild variant="outline" className="mt-4">
                    <Link to="/admin/submissions">Back to submissions</Link>
                </Button>
            </DashboardShell>
        );
    }

    const canApproveReject = sub.status === "submitted";
    const canReopen = sub.status === "submitted" || sub.status === "rejected";
    const reviewedRecord = sub.reviewed_at;

    return (
        <DashboardShell>
            <div className="flex items-start justify-between flex-wrap gap-4 fade-up">
                <div>
                    <Link to="/admin/submissions" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-3">
                        <ChevronLeft size={14} /> All submissions
                    </Link>
                    <p className="text-xs font-medium tracking-[0.18em] uppercase text-blue-900">
                        Submission · {sub.id.slice(0, 8)}
                    </p>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
                        {sub.data?.company_name || sub.customer_name || "Application"}
                    </h1>
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-slate-600">
                        <span
                            data-testid="admin-submission-status"
                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ring-1 ring-inset ${STATUS_PILL[sub.status]}`}
                        >
                            {sub.status}
                        </span>
                        <span>·</span>
                        <span>{sub.customer_name} ({sub.customer_email})</span>
                        {sub.submitted_at && (
                            <>
                                <span>·</span>
                                <span>Submitted {fmtDateTime(sub.submitted_at)}</span>
                            </>
                        )}
                    </div>
                    <div className="mt-3">
                        <EquipmentTypeBadge data={sub.data} variant="line" testId="admin-submission-equipment" />
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Button
                        variant="outline"
                        onClick={downloadPdf}
                        data-testid="admin-download-pdf"
                        className="gap-1.5"
                    >
                        <Download size={14} /> PDF
                    </Button>
                    {/* Phase AE — "Generate contract" button removed. The
                        approved → assign-equipment → pending-contract
                        handoff now lives below the actions row. */}
                    {sub.status === "approved" && (
                        <Button
                            onClick={() => setAssignEquipmentOpen(true)}
                            data-testid="admin-assign-equipment-btn"
                            className="bg-blue-900 hover:bg-blue-800 gap-1.5 text-white"
                        >
                            <Truck size={14} /> Assign Equipment
                        </Button>
                    )}
                    {canApproveReject && (
                        <>
                            <Button
                                onClick={() => openDialog("approve")}
                                data-testid="admin-approve-button"
                                className="bg-emerald-700 hover:bg-emerald-800 gap-1.5"
                            >
                                <CheckCircle2 size={14} /> Approve
                            </Button>
                            <Button
                                onClick={() => { setDialog("reject"); setNote(""); }}
                                data-testid="admin-reject-button"
                                className="bg-red-600 hover:bg-red-700 gap-1.5"
                            >
                                <XCircle size={14} /> Reject
                            </Button>
                        </>
                    )}
                    {canReopen && (
                        <Button
                            variant="outline"
                            onClick={() => { setDialog("reopen"); setNote(""); }}
                            data-testid="admin-reopen-button"
                            className="gap-1.5"
                        >
                            <RotateCcw size={14} /> Reopen
                        </Button>
                    )}
                    {/* Phase CANCEL-DRAFT (Part B / FE) — admin cancel of a draft submission. */}
                    {sub.status === "draft" && hasPermission("Submissions", "Cancel draft") && (
                        <Button
                            variant="outline"
                            onClick={() => setCancelDraftOpen(true)}
                            data-testid="admin-cancel-draft-button"
                            className="gap-1.5 text-red-700 border-red-200 hover:bg-red-50 hover:text-red-800"
                        >
                            <XCircle size={14} /> Cancel draft
                        </Button>
                    )}
                </div>
            </div>

            <CancelSubmissionModal
                open={cancelDraftOpen}
                onOpenChange={setCancelDraftOpen}
                target={sub ? { entityType: "submission", id: sub.id } : null}
                surface="admin"
                fieldsFilledCount={sub ? Object.values(sub?.data || {}).filter((v) => v != null && v !== "").length : 0}
                filesCount={sub?.uploads?.length || 0}
                companyInfoPresent={!!(sub?.data?.company_name || sub?.data?.company_legal_name)}
                guarantorInfoPresent={!!(sub?.data?.guarantor_name || sub?.data?.guarantor_ssn_last4)}
                onCancelled={async () => { await reload(); }}
            />

            {/* Task 27 Bug-2 — once a submission has been cancelled
                (awaiting_reassignment or cancelled_by_admin), the
                green "review" banner is no longer the dominant signal;
                the new amber/red card below is. Hide the review banner
                in those terminal-cancelled states so we don't show
                conflicting context. */}
            {reviewedRecord && sub.status !== "awaiting_reassignment" && sub.status !== "cancelled_by_admin" && (
                <Card className="mt-6" data-testid="review-banner">
                    <CardContent className="p-5 flex items-start gap-3">
                        <Mail size={18} className="text-slate-500 mt-0.5" />
                        <div className="text-sm">
                            <div className="font-medium text-slate-900">
                                {sub.status === "approved" ? "Approved" : sub.status === "rejected" ? "Rejected" : sub.status === "equipment_assigned" ? "Approved & Equipment Assigned" : "Reopened"} · {fmtDateTime(sub.reviewed_at)}
                            </div>
                            {(sub.reviewer_name || sub.reviewer_email) && (
                                <div className="text-xs text-slate-500 mt-0.5" data-testid="review-banner-reviewer">
                                    by {sub.reviewer_name || "—"}
                                    {sub.reviewer_email ? ` · ${sub.reviewer_email}` : ""}
                                </div>
                            )}
                            {sub.review_note && <div className="mt-2 text-slate-700 whitespace-pre-wrap">{sub.review_note}</div>}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Phase AE — read-only banner once equipment is assigned. */}
            {sub.status === "equipment_assigned" && sub.customer_id && (
                <Card
                    className="mt-4 border-blue-200 bg-blue-50"
                    data-testid="equipment-assigned-banner"
                >
                    <CardContent className="p-5 flex items-start gap-3">
                        <CheckCircle2 size={20} className="text-blue-900 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 text-sm">
                            <div className="font-medium text-blue-950">
                                Equipment assigned · Contract is now pending
                            </div>
                            <div className="mt-1 text-blue-900/80 text-xs">
                                A draft rental and pending contract have been created in
                                {" "}<strong>{customerNameFromSub || "this customer"}</strong>&apos;s
                                Contracts tab. Open it to pick a template and generate the contract.
                            </div>
                            <Link
                                to={`/admin/customers/${sub.customer_id}?tab=contracts`}
                                data-testid="equipment-assigned-go-to-contracts"
                                className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-blue-900 hover:underline"
                            >
                                Open {customerNameFromSub || "customer"}&apos;s Contracts
                                <ExternalLink size={12} />
                            </Link>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Task 24-B4 — Awaiting reassignment after a contract was
                cancelled with action=reassign. Admin needs a one-click
                way back to either pick new equipment or fully archive. */}
            {sub.status === "awaiting_reassignment" && sub.customer_id && (
                <Card
                    className="mt-4 border-amber-200 bg-amber-50"
                    data-testid="awaiting-reassignment-banner"
                >
                    <CardContent className="p-5 space-y-3">
                        <div className="flex items-start gap-3">
                            <AlertTriangle size={20} className="text-amber-900 mt-0.5 flex-shrink-0" />
                            <div className="flex-1 text-sm">
                                <div className="font-medium text-amber-950">
                                    Contract cancelled · {sub.cancelled_at ? fmtDateTime(sub.cancelled_at) : ""}
                                    {sub.cancelled_by_email ? ` by ${sub.cancelled_by_email}` : ""}
                                </div>
                                {sub.cancellation_reason && (
                                    <div className="mt-1 text-amber-900/90 text-xs">
                                        Reason: &ldquo;{sub.cancellation_reason}&rdquo;
                                    </div>
                                )}
                                <div className="mt-2 text-amber-900/80 text-xs">
                                    The original equipment was freed and the draft rental dropped.
                                    Pick new equipment to continue, or archive the submission to close it out.
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            {/* Task 28-D — Reassign now opens the SAME
                                equipment-picker dialog used by the
                                "Assign Equipment" button on approved
                                submissions. Old behaviour navigated
                                away to the contracts tab which broke
                                the in-context flow. */}
                            <button
                                type="button"
                                onClick={() => setAssignEquipmentOpen(true)}
                                data-testid="awaiting-reassignment-reassign"
                                className="inline-flex items-center gap-1 rounded-md bg-amber-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-800"
                            >
                                <Truck size={12} /> Reassign equipment
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowArchiveAwaiting(true)}
                                data-testid="awaiting-reassignment-archive"
                                className="inline-flex items-center gap-1 rounded-md border border-amber-700 px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
                                title="Mark this submission as cancelled without reassigning new equipment."
                            >
                                Archive submission
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowResendInvite(true)}
                                data-testid="awaiting-reassignment-resend-invite"
                                className="inline-flex items-center gap-1 rounded-md border border-amber-700 px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
                                title="Email a fresh application invite to the customer using the same form."
                            >
                                <Mail size={12} /> Resend invitation
                            </button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Task 24-B4 — cancelled_by_admin terminal state — read-only. */}
            {sub.status === "cancelled_by_admin" && (
                <Card
                    className="mt-4 border-red-200 bg-red-50"
                    data-testid="cancelled-by-admin-banner"
                >
                    <CardContent className="p-5 space-y-3">
                        <div className="flex items-start gap-3">
                            <AlertTriangle size={20} className="text-red-900 mt-0.5 flex-shrink-0" />
                            <div className="flex-1 text-sm">
                                <div className="font-medium text-red-950">
                                    Submission archived · {sub.cancelled_at ? fmtDateTime(sub.cancelled_at) : ""}
                                    {sub.cancelled_by_email ? ` by ${sub.cancelled_by_email}` : ""}
                                </div>
                                {sub.cancellation_reason && (
                                    <div className="mt-1 text-red-900/90 text-xs">
                                        Reason: &ldquo;{sub.cancellation_reason}&rdquo;
                                    </div>
                                )}
                                <div className="mt-2 text-red-900/80 text-xs">
                                    The customer has been told the application is closed. Click below to send them a fresh invite.
                                </div>
                            </div>
                        </div>
                        {sub.resent_as_invite_id ? (
                            <div
                                className="rounded-md border border-red-300 bg-white px-3 py-2 text-xs text-red-900"
                                data-testid="cancelled-by-admin-already-resent"
                            >
                                A fresh invitation was already sent for this application.
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={() => setShowResendInvite(true)}
                                data-testid="cancelled-by-admin-resend-invite"
                                className="inline-flex items-center gap-1 rounded-md bg-red-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-800 w-fit"
                            >
                                <Mail size={12} /> Resend invitation
                            </button>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Task 26-A — confirm modal for the resend-invitation button. */}
            <Dialog open={showResendInvite} onOpenChange={setShowResendInvite}>
                <DialogContent data-testid="resend-invite-dialog" className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Resend invitation</DialogTitle>
                        <DialogDescription>
                            We&apos;ll email a fresh application invite that uses the same form.
                            The customer can start over without losing their seat in your pipeline.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-sm">
                            <span className="text-slate-500">To:</span>{" "}
                            <span className="font-medium text-slate-900" data-testid="resend-invite-target-email">
                                {customerEmailFromSub || "the customer on file"}
                            </span>
                        </div>
                        <div>
                            <Label htmlFor="resend-invite-note" className="text-xs">
                                Internal note (optional) <span className="text-slate-500">— not shown to customer</span>
                            </Label>
                            <Textarea
                                id="resend-invite-note"
                                value={resendInviteNote}
                                onChange={(e) => setResendInviteNote(e.target.value)}
                                placeholder="Why are we resending? (audit trail)"
                                rows={2}
                                data-testid="resend-invite-note-input"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowResendInvite(false)} data-testid="resend-invite-cancel">
                            Cancel
                        </Button>
                        <Button
                            onClick={onResendInvitation}
                            disabled={resendingInvite}
                            data-testid="resend-invite-confirm"
                        >
                            {resendingInvite ? "Sending…" : "Send invite"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Task 28-D — Archive awaiting-reassignment confirm. Flips
                the submission to cancelled_by_admin (terminal). No
                contract action is needed because the contract that put
                us here is already status=cancelled. */}
            <Dialog open={showArchiveAwaiting} onOpenChange={setShowArchiveAwaiting}>
                <DialogContent data-testid="archive-awaiting-dialog" className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Archive submission?</DialogTitle>
                        <DialogDescription>
                            Mark this submission as cancelled without reassigning new equipment.
                            This is terminal — the customer will only be able to start a fresh
                            application via a new invite.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label htmlFor="archive-awaiting-note" className="text-xs">
                                Reason / note (optional)
                            </Label>
                            <Textarea
                                id="archive-awaiting-note"
                                value={archiveAwaitingNote}
                                onChange={(e) => setArchiveAwaitingNote(e.target.value)}
                                placeholder="Why are we archiving? (audit trail; optionally shown to customer)"
                                rows={2}
                                data-testid="archive-awaiting-note-input"
                            />
                        </div>
                        <label className="flex items-start gap-2 text-xs text-slate-700">
                            <input
                                type="checkbox"
                                checked={archiveAwaitingVisible}
                                onChange={(e) => setArchiveAwaitingVisible(e.target.checked)}
                                data-testid="archive-awaiting-visible-checkbox"
                                className="mt-0.5"
                            />
                            <span>Show this reason to the customer on their dashboard.</span>
                        </label>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            disabled={archivingAwaiting}
                            onClick={() => setShowArchiveAwaiting(false)}
                            data-testid="archive-awaiting-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={onArchiveAwaiting}
                            disabled={archivingAwaiting}
                            data-testid="archive-awaiting-confirm"
                            className="bg-red-700 text-white hover:bg-red-600"
                        >
                            {archivingAwaiting ? "Archiving…" : "Archive submission"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-5">
                {["company", "guarantor", "references"].map((g) => (
                    <SectionCard
                        key={g}
                        icon={g === "company" ? Building2 : g === "guarantor" ? User : BookOpenText}
                        title={GROUP_LABELS[g]}
                    >
                        {(grouped[g] || []).map((f) => {
                            const fmcsaRef = fmcsaFieldFor(f.key);
                            // Phase QCM-EIN — EIN comparison is digit-only.
                            // Non-EIN fields keep exact-string equality.
                            let fmcsaState = "none";
                            if (f.key === "ein_tax_id") {
                                if (fmcsaRef !== null && fmcsaRef !== undefined) {
                                    const a = normalizeEin(sub.data?.[f.key]);
                                    const b = normalizeEin(fmcsaRef);
                                    if (b.length === 9) {
                                        fmcsaState = a === b ? "verified" : (a.length > 0 ? "diverged" : "diverged");
                                    }
                                }
                            } else if (fmcsaRef !== null && fmcsaRef !== undefined) {
                                fmcsaState = classifyFmcsaField(sub.data?.[f.key], fmcsaRef);
                            }
                            // Phase QCM-EIN — render the EIN cell in
                            // canonical XX-XXXXXXX form even if storage
                            // still has the raw digits.
                            const displayValue = (f.key === "ein_tax_id" && sub.data?.[f.key])
                                ? formatEin(sub.data[f.key])
                                : renderValue(f);
                            return (
                                <FieldRow
                                    key={f.key}
                                    label={f.label}
                                    value={displayValue}
                                    required={cfg[f.key] === "required"}
                                    fmcsaState={fmcsaState}
                                    testId={`review-field-row-${f.key}`}
                                />
                            );
                        })}
                        {(!grouped[g] || grouped[g].length === 0) && (
                            <div className="px-4 py-6 text-sm text-slate-500 text-center">No fields configured.</div>
                        )}
                    </SectionCard>
                ))}

                <SectionCard icon={UploadCloud} title="Uploads">
                    {(grouped.uploads || []).map((f) => {
                        const u = (sub.uploads || []).find((x) => x.field_key === f.key);
                        return (
                            <div key={f.key} className="px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 text-sm" data-testid={`upload-row-${f.key}`}>
                                <span className="text-slate-500">{f.label}{cfg[f.key] === "required" && <span className="text-red-500 ml-1">*</span>}</span>
                                {u ? (
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 min-w-0">
                                        <UploadStatusBadge
                                            status={u.status}
                                            reason={u.reason}
                                            fieldKey={f.key}
                                        />
                                        <div className="flex items-center gap-2 shrink-0">
                                            <Button
                                                type="button"
                                                size="sm"
                                                variant="outline"
                                                onClick={() => setPreviewFile(u)}
                                                data-testid={`preview-upload-${f.key}`}
                                                className="gap-1.5"
                                            >
                                                <Eye size={12} /> Preview
                                            </Button>
                                            <button
                                                type="button"
                                                onClick={() => downloadUpload(u)}
                                                data-testid={`download-upload-${f.key}`}
                                                className="inline-flex items-center gap-1.5 text-blue-900 hover:underline text-xs max-w-[16rem] truncate"
                                                title={u.filename}
                                            >
                                                <Download size={12} /> {u.filename}
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <span className="text-slate-400">— not provided</span>
                                )}
                            </div>
                        );
                    })}
                    {(!grouped.uploads || grouped.uploads.length === 0) && (
                        <div className="px-4 py-6 text-sm text-slate-500 text-center">No uploads configured.</div>
                    )}
                </SectionCard>
            </div>

            {/* Signatures */}
            <div className="mt-5">
                <SectionCard icon={PenTool} title="Signatures">
                    {(grouped.signatures || []).map((f) => {
                        const s = (sub.signatures || []).find((x) => x.field_key === f.key);
                        return (
                            <div key={f.key} className="px-4 py-4 flex justify-between items-center gap-4" data-testid={`sig-row-${f.key}`}>
                                <span className="text-sm text-slate-500">{f.label}{cfg[f.key] === "required" && <span className="text-red-500 ml-1">*</span>}</span>
                                {s ? (
                                    <div className="flex items-center gap-3">
                                        {s.method === "draw" && s.image_data_url ? (
                                            <img
                                                src={s.image_data_url}
                                                alt="Signature"
                                                data-testid={`sig-image-${f.key}`}
                                                className="h-12 w-32 object-contain bg-white border border-slate-200 rounded"
                                            />
                                        ) : (
                                            <span
                                                className="h-12 w-32 flex items-center justify-center text-2xl bg-white border border-slate-200 rounded"
                                                style={{ fontFamily: s.font || "cursive" }}
                                                data-testid={`sig-text-${f.key}`}
                                            >
                                                {s.typed_text}
                                            </span>
                                        )}
                                        <div className="text-xs">
                                            <div className="font-medium text-slate-900">{s.signer_name}</div>
                                            <div className="text-slate-500">{s.method} · {fmtDateTime(s.signed_at)}</div>
                                        </div>
                                    </div>
                                ) : (
                                    <span className="text-sm text-slate-400">— not signed</span>
                                )}
                            </div>
                        );
                    })}
                    {(!grouped.signatures || grouped.signatures.length === 0) && (
                        <div className="px-4 py-6 text-sm text-slate-500 text-center">No signatures configured.</div>
                    )}
                </SectionCard>
            </div>

            {sub.invite_id && (
                <div className="mt-5 text-sm text-slate-500">
                    From invite{" "}
                    <Link to="/admin/invites" className="text-blue-900 hover:underline inline-flex items-center gap-1">
                        {sub.invite_id.slice(0, 8)} <ExternalLink size={12} />
                    </Link>
                </div>
            )}

            {/* Action dialogs */}
            <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
                <DialogContent data-testid="review-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display capitalize">
                            {dialog === "approve" ? "Approve application" : dialog === "reject" ? "Reject application" : "Reopen application"}
                        </DialogTitle>
                        <DialogDescription>
                            {dialog === "approve"
                                ? "Mark this application as approved. You can leave an optional note for the record."
                                : dialog === "reject"
                                  ? "Reject this application. A note explaining the reason is required."
                                  : "Send this application back to the customer for edits. They will receive an email with your note."}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        {dialog === "approve" && saferPreflight?.kind === "risk" && (
                            <div
                                className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900"
                                data-testid="approve-dialog-safer-warning"
                            >
                                <p className="font-semibold flex items-center gap-1.5">
                                    <ShieldAlert size={14} /> FMCSA SAFER flag
                                    {saferPreflight.legal_name && (
                                        <span className="font-normal text-red-800/80"> · {saferPreflight.legal_name}</span>
                                    )}
                                </p>
                                <ul className="mt-1 list-disc ml-5 space-y-0.5 text-xs">
                                    {saferPreflight.reasons.map((r) => (
                                        <li key={r}>{SAFER_RISK_LABELS[r] || r}</li>
                                    ))}
                                </ul>
                                <p className="mt-2 text-xs text-red-800/80">
                                    This carrier's authority may be compromised. You can still approve, but clicking the button below counts as an acknowledgment.
                                </p>
                            </div>
                        )}
                        {dialog === "approve" && saferPreflight?.kind === "unavailable" && (
                            <div
                                className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900"
                                data-testid="approve-dialog-safer-unavailable"
                            >
                                SAFER pre-flight did not complete in time — proceeding without FMCSA verification.
                            </div>
                        )}
                        <div className="space-y-2">
                        <Label htmlFor="review_note">
                            Note {(dialog === "reject" || dialog === "reopen") && <span className="text-red-500">*</span>}
                        </Label>
                        <Textarea
                            id="review_note"
                            data-testid="review-note-input"
                            rows={4}
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder={
                                dialog === "approve"
                                    ? "Optional notes for the file…"
                                    : dialog === "reject"
                                      ? "Explain why this is being rejected…"
                                      : "Tell the customer what to update…"
                            }
                        />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialog(null)} data-testid="review-dialog-cancel">
                            Cancel
                        </Button>
                        <Button
                            onClick={submitAction}
                            disabled={busy}
                            data-testid="review-dialog-confirm"
                            className={
                                dialog === "approve"
                                    ? (saferPreflight?.kind === "risk"
                                        ? "bg-red-600 hover:bg-red-700"
                                        : "bg-emerald-700 hover:bg-emerald-800")
                                    : dialog === "reject"
                                      ? "bg-red-600 hover:bg-red-700"
                                      : "bg-blue-900 hover:bg-blue-800"
                            }
                        >
                            {busy
                                ? "Working…"
                                : dialog === "approve"
                                  ? (saferPreflight?.kind === "risk" ? "Approve despite risk" : "Approve")
                                  : dialog === "reject"
                                    ? "Reject"
                                    : "Reopen"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <GenerateContractDialog
                submission={sub}
                open={contractDialogOpen}
                onOpenChange={setContractDialogOpen}
            />

            <FilePreviewModal
                open={previewOpen}
                onOpenChange={(o) => !o && setPreviewFile(null)}
                file={previewFile}
                submissionId={sub.id}
                mode="admin"
                onDecision={(updated) => {
                    // Patch the local sub.uploads in place so the badge
                    // flips without a full reload round-trip; we still
                    // call reload() to keep the audit-trail-derived
                    // fields in sync if anything else changed.
                    setSub((prev) => prev && ({
                        ...prev,
                        uploads: (prev.uploads || []).map((u) =>
                            u.id === updated.id ? { ...u, ...updated } : u
                        ),
                    }));
                    reload();
                }}
            />

            {/* Phase AE — Assign Equipment dialog. Only ever opens when
                the submission is in ``approved`` status (gate enforced
                by the trigger button + by the backend's precondition). */}
            <AssignEquipmentDialog
                open={assignEquipmentOpen}
                onOpenChange={setAssignEquipmentOpen}
                submissionId={sub?.id}
                customerName={customerNameFromSub}
                defaultEquipmentType={sub?.data?.equipment_type}
                onSuccess={() => {
                    // Refetch so the page flips into the
                    // equipment_assigned banner state.
                    reload();
                }}
            />
        </DashboardShell>
    );
}
